import { isSectionHeader, type EstimateLine, type OfferRow } from "@/types/mvpEstimate";
import { lineFinalPrice, lineGrossPrice, roundMoney } from "@/utils/mvpEstimateMath";

export function fmtPln(n: number): string {
  return n.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function lineUnitNetAfterMargin(line: EstimateLine): number {
  if (line.quantity <= 0) return 0;
  return roundMoney(lineFinalPrice(line) / line.quantity);
}

export function formatPlDate(d: Date): string {
  return d.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function buildOfferNumber(draftId: string | undefined, d: Date): string {
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const id = draftId?.trim() ?? "";
  const tailRaw = id.slice(-3);
  const tail = tailRaw ? tailRaw.toUpperCase().padStart(3, "0") : "001";
  return `OF / ${yyyy} / ${mm} / ${tail}`;
}

export function buildVatLabel(rates: number[]): string {
  const uniq = [...new Set(rates.map((r) => Math.round(r * 1000) / 1000))];
  if (uniq.length === 1) {
    const r = uniq[0];
    if (Math.abs(r - 0.08) < 0.001) return "VAT 8%";
    if (Math.abs(r - 0.23) < 0.001) return "VAT 23%";
    if (Math.abs(r) < 0.001) return "VAT 0%";
    return `VAT ${Math.round(r * 100)}%`;
  }
  return "VAT";
}

export function pluralPozycje(n: number): string {
  if (n === 1) return "pozycja";
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "pozycje";
  return "pozycji";
}

export function normalizeAssumptions(items: string[] | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items ?? []) {
    const value = raw.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

export function payableLines(lines: OfferRow[]): EstimateLine[] {
  return lines.filter((row): row is EstimateLine => !isSectionHeader(row) && lineGrossPrice(row) > 0);
}

export function groupPayableRows(lines: OfferRow[]): Array<{ title: string | null; lines: EstimateLine[] }> {
  const groups: Array<{ title: string | null; lines: EstimateLine[] }> = [];
  let currentTitle: string | null = null;
  let currentLines: EstimateLine[] = [];
  const pushGroup = () => {
    if (currentLines.length === 0) return;
    groups.push({ title: currentTitle, lines: currentLines });
    currentTitle = null;
    currentLines = [];
  };
  for (const row of lines) {
    if (isSectionHeader(row)) {
      pushGroup();
      currentTitle = row.title.trim() || null;
      continue;
    }
    if (lineGrossPrice(row) > 0) currentLines.push(row);
  }
  pushGroup();
  return groups;
}

